import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

/*
  Scroll behaviour for the live call list.

  The list is sorted newest-first, so every new call - whether it arrives over
  the socket or is paged in by the "load newer" sentinel - is inserted at the
  very top, pushing everything below it down. Left alone, that means the row a
  user is reading walks down the screen every few seconds, and any attempt to
  scroll away is undone the moment the next call lands.

  Two independent jobs, kept separate on purpose:

  1. Anchoring - after any list update, put the content the user was looking at
     back where it was. Positions are compared in *document* coordinates, which
     the user's own scrolling does not change, so only rows inserted above the
     anchor move it. (An earlier version compared viewport coordinates, which
     also move when the user scrolls, so every incoming call scrolled them back
     to where they started - the list felt stuck.)

  2. Following - the list is "following" live until the user scrolls away from
     the top, and follows again once they come back to it. Only while following
     do we chase the playing call; while detached we never move the page under
     them, and instead count what arrived and offer a jump back to the top.
*/

// The menu across the top and the player bar across the bottom are both fixed,
// so the part of the window a call can actually be read in lies between them.
const MENU_HEIGHT = 55;
const PLAYER_HEIGHT = 55;
// Where the row under the user's eye sits - just below the top menu.
const TOP_OFFSET = 70;
// Within this many pixels of the top counts as parked at the top of the feed.
const TOP_ZONE = 8;
// Scroll offsets can be fractional (zoom, hidpi); a bigger jump than this means
// the page moved for some reason other than the scroll we just performed.
const USER_SCROLL_SLOP = 2;
// How long to treat scroll events as our own after starting an animated scroll.
const SMOOTH_SCROLL_MS = 800;
// Scrolling further than this in one animated jump is done instantly instead.
const SMOOTH_SCROLL_MAX_SCREENS = 3;

// Document-relative top of an element. Unlike getBoundingClientRect().top this
// does not change when the page is scrolled, so comparing it across renders
// isolates movement caused by rows being inserted above it.
const docTopOf = (node) => node.getBoundingClientRect().top + window.scrollY;

// Where the playing call is parked: centred in the readable band between the two
// fixed bars. Newer calls stack up in the space above it as they arrive, which
// is how you can see how far behind live the player is running.
const restingPlaceFor = (rowHeight) =>
  MENU_HEIGHT + (window.innerHeight - MENU_HEIGHT - PLAYER_HEIGHT - rowHeight) / 2;

export default function useLiveScroll({ listRef, callsData, activeCallId, activeCallTime }) {
  const centerCall = useSelector((state) => state.callPlayer.centerCall);
  // Bumped by the calls slice whenever a page of *newer* calls is prepended
  // (as opposed to a live call arriving), and whenever the list is replaced.
  const prependVersion = useSelector((state) => state.calls.prependVersion);
  const resetVersion = useSelector((state) => state.calls.resetVersion);

  const [following, setFollowing] = useState(true);
  const [newCallCount, setNewCallCount] = useState(0);

  // `following` is read from a layout effect and a scroll listener that must
  // both see the current value, not the one captured at their last render.
  const followingRef = useRef(true);
  // Where we believe the page is, so a scroll to anywhere else is the user's.
  const expectedYRef = useRef(typeof window === "undefined" ? 0 : window.scrollY);
  const smoothUntilRef = useRef(0);

  const anchorRef = useRef(null); // { id, docTop } of the top row, pre-update
  const previousActiveIdRef = useRef(null);
  const previousActiveTimeRef = useRef(null);
  const previousFirstIdRef = useRef(null);
  const previousPrependRef = useRef(prependVersion);
  const previousResetRef = useRef(resetVersion);

  const rowFor = useCallback(
    (callId) => {
      if (!callId || !listRef.current) return null;
      return listRef.current.querySelector(`tr[data-callid="${callId}"]`);
    },
    [listRef]
  );

  // The row sitting at the top of the readable area is the one the user is
  // looking at, and the one that has to stay still. Probe for it just inside the
  // left edge of the table, clear of the "new calls" banner floating over the
  // middle of the page. If there is nothing to find there - the list is covered
  // by a modal, or scrolled off - fall back to the top row, which any insert
  // above the list moves by the same amount anyway.
  const findAnchor = useCallback(() => {
    const list = listRef.current;
    if (!list) return null;

    const probe = document.elementFromPoint(list.getBoundingClientRect().left + 8, TOP_OFFSET);
    const row = probe && probe.closest ? probe.closest("tr[data-callid]") : null;
    if (row && list.contains(row)) {
      return { id: row.dataset.callid, docTop: docTopOf(row) };
    }

    const firstRow = list.querySelector("tr[data-callid]");
    return firstRow ? { id: firstRow.dataset.callid, docTop: docTopOf(firstRow) } : null;
  }, [listRef]);

  const scrollByPixels = useCallback((delta) => {
    // Recorded either side of the scroll: afterwards to pick up any clamping the
    // browser applied, and beforehand so that a scroll event delivered while we
    // are still inside scrollBy cannot be mistaken for the user moving the page.
    expectedYRef.current = window.scrollY + delta;
    window.scrollBy({ top: delta, behavior: "auto" });
    expectedYRef.current = window.scrollY;
  }, []);

  const attach = useCallback((y) => {
    followingRef.current = true;
    setFollowing(true);
    setNewCallCount((count) => (count === 0 ? count : 0));
    if (typeof y === "number") expectedYRef.current = y;
  }, []);

  // Take the user back to the newest calls.
  const jumpToLive = useCallback(() => {
    const smooth = window.scrollY < window.innerHeight * SMOOTH_SCROLL_MAX_SCREENS;
    smoothUntilRef.current = smooth ? performance.now() + SMOOTH_SCROLL_MS : 0;
    window.scrollTo({ top: 0, behavior: smooth ? "smooth" : "auto" });
    attach(smooth ? undefined : window.scrollY);
  }, [attach]);

  // Detaching from live is driven by comparing where the page ended up against
  // where we put it: that catches every way a user can scroll (wheel, touch,
  // keyboard, dragging the scrollbar) without having to listen for each one,
  // and ignores the scrolling this hook does itself.
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;

      // Back at the newest calls: following live again.
      if (y <= TOP_ZONE) {
        attach(y);
        return;
      }

      // One of our own animated scrolls is still running.
      if (performance.now() < smoothUntilRef.current) {
        expectedYRef.current = y;
        return;
      }

      // The page is somewhere we did not put it, so the user moved it. Note that
      // expectedYRef is deliberately not updated here: a slow scroll arrives a
      // pixel or two at a time, and re-baselining on each event would let it
      // creep down the page without ever tripping the threshold.
      if (followingRef.current && Math.abs(y - expectedYRef.current) > USER_SCROLL_SLOP) {
        followingRef.current = false;
        setFollowing(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [attach]);

  useLayoutEffect(() => {
    const ids = callsData ? callsData.ids : [];
    const firstId = ids.length > 0 ? ids[0] : null;

    const remember = () => {
      anchorRef.current = findAnchor();
      previousFirstIdRef.current = firstId;
      previousActiveIdRef.current = activeCallId;
      previousActiveTimeRef.current = activeCallTime;
    };

    // The list was replaced wholesale - different system, filter or archive
    // date. Nothing that was on screen is meaningful any more, so start over at
    // the top, following live again.
    if (resetVersion !== previousResetRef.current) {
      previousResetRef.current = resetVersion;
      previousPrependRef.current = prependVersion;
      window.scrollTo({ top: 0, behavior: "auto" });
      attach(window.scrollY);
      remember();
      return;
    }

    const pagedPrepend = prependVersion !== previousPrependRef.current;
    previousPrependRef.current = prependVersion;

    const anchor = anchorRef.current;
    const anchorNode = anchor ? rowFor(anchor.id) : null;
    const animating = performance.now() < smoothUntilRef.current;

    // 1. The anchor row moved down the document, so rows landed above it: shift
    //    the page by the same amount and the content under the user's eyes does
    //    not move. Two exceptions: while the user is parked at the top of a live
    //    feed, where new calls *should* push the page down so they can watch
    //    them arrive; and while one of our own animated scrolls is still
    //    running, which scrolling now would cancel.
    if (anchorNode && !animating) {
      const shift = docTopOf(anchorNode) - anchor.docTop;
      const parkedAtTop = window.scrollY <= TOP_ZONE;
      if (shift !== 0 && (pagedPrepend || !parkedAtTop)) {
        scrollByPixels(shift);
      }
    }

    // 2. Count live calls that arrived above a user who is reading older ones,
    //    so the banner can offer to take them back up. Calls paged in by the
    //    "load newer" sentinel are not new arrivals, so they do not count.
    const previousFirstId = previousFirstIdRef.current;
    if (
      !followingRef.current &&
      !pagedPrepend &&
      firstId &&
      previousFirstId &&
      firstId !== previousFirstId
    ) {
      const added = ids.indexOf(previousFirstId);
      if (added > 0) setNewCallCount((count) => count + added);
    }

    // 3. Park the playing call in one fixed place and let the list flow past it,
    //    which is what "centre call" means and what makes a run of calls read as
    //    one conversation. Only while following - a user who has scrolled away
    //    is never pulled back - and the scroll is clamped by the browser, so
    //    calls near the top of the list simply stay where they are.
    //
    //    The view only ever follows *forwards*. A call that reaches us late -
    //    a long one, which finishes uploading after the short calls that
    //    started after it - is inserted well down the list, and dragging the
    //    page down to it would throw the reader back through calls they have
    //    already heard. It still plays; the page just stays where it is.
    //    A call the user clicked is older too, and is by definition already on
    //    screen, so it is left alone for the same reason.
    const activeChanged = activeCallId !== previousActiveIdRef.current;
    const previousActiveTime = previousActiveTimeRef.current;
    const movedForward =
      !previousActiveTime ||
      !activeCallTime ||
      new Date(activeCallTime).getTime() >= new Date(previousActiveTime).getTime();

    if (activeChanged && activeCallId && centerCall && followingRef.current && movedForward) {
      const activeNode = rowFor(activeCallId);
      if (activeNode) {
        const rect = activeNode.getBoundingClientRect();
        const delta = rect.top - restingPlaceFor(rect.height);
        if (Math.abs(delta) > 1) {
          scrollByPixels(delta);
        }
      }
    }

    remember();
  }, [
    callsData,
    activeCallId,
    activeCallTime,
    centerCall,
    prependVersion,
    resetVersion,
    attach,
    rowFor,
    findAnchor,
    scrollByPixels,
  ]);

  return { following, newCallCount, jumpToLive };
}
